import React, { createContext, useContext, useState } from "react";

const translations = {
  en: {
    langToggle: "中文",

    // App loading
    loading: "Loading…",

    // Common
    cancel: "Cancel",
    delete: "Delete",
    confirm: "Confirm",
    open: "Open",
    back: "Back",

    // Header profile
    myProfile: "My Profile",

    // AiPanel tabs
    tabExplain: "💡 Explain",
    tabSummary: "📝 Summary",
    tabTranslate: "🌍 Translate",
    tabChat: "💬 Chat",
    tabNotes: "📋 Notes",
    noBookNotice: "Upload a document to use this feature",

    // SummaryTab
    pageRangeLabel: "Page Range",
    pageRangeTo: "to",
    promptBtn: "⚙️ Prompt",
    promptBtnTooltip: "Customise AI prompt template",
    ptTitle: "Prompt Template Settings",
    ptSubtitle: "Customise how the AI responds \u2014 pick a preset or write your own.",
    ptPresets: "Presets",
    ptMyPresets: "My Presets",
    ptBuiltIn: "Built-in",
    ptCustomPreset: "Custom preset",
    ptTemplateEditor: "Template Editor",
    ptCustomBadge: "Custom",
    ptResetDefault: "Reset to default",
    ptAvailableVars: "Available variables:",
    ptSaveAsPreset: "Save as preset",
    ptPresetNamePlaceholder: "Preset name...",
    ptAdd: "Add",
    ptCancel: "Cancel",
    ptSaveApply: "Save & Apply",
    summarizeBtn: "📝 Summarize with Analogy",
    followUpPlaceholder: "Ask a follow-up question...",
    askBtn: "↩ Ask",
    invalidPageRange: "Invalid page range",
    tooManyPagesSummary: "Max 5 pages per summary",
    tooManyPagesTranslate: "Max 5 pages per translation",
    maxPagesHint: "(max 5 pages)",
    summaryFailed: "Summary failed",
    failSummary: "Failed to generate summary",
    failFollowUp: "Failed to send follow-up",
    followUpFailed: "Follow-up failed",

    // QuizTab
    numQuestionsLabel: "Number of Questions",
    generateQuizBtn: "🎯 Generate Quiz",
    quizFailed: "Quiz generation failed",
    failQuiz: "Failed to generate quiz",

    // TranslateTab
    targetLanguageLabel: "Target Language",
    translateBtn: "🌍 Translate",
    translationFailed: "Translation failed",
    failTranslate: "Failed to translate",

    // ExplainTab
    explainConceptLabel: "What concept do you want explained?",
    conceptPlaceholder: "e.g. Machine Learning, Photosynthesis, Supply & Demand…",
    explainBtn: "💡 Explain",
    explainSimpleBtn: "🌱 Explain Simply",
    followUpPlaceholderExplain: "Ask a follow-up or attach files…",
    enterConceptFirst: "Please enter a concept or attach a file",
    explainFailed: "Explain failed",
    failExplain: "Failed to explain concept",

    // ChatTab
    chatEmptyTitle: "Ask me anything — questions, images, or files.",
    chatEmptyHint: "Attach images, PDFs, Word, Excel, CSV, or PowerPoint files.",
    chatInputPlaceholder: "Ask anything, or paste an image here (Ctrl+V)...",
    clearChat: "🗑 Clear chat",
    addToNotes: "📌 Add to Notes",
    chatError: "Sorry, something went wrong. Please try again.",

    // PdfPanel
    uploadTab: "📤 Upload",
    uploadedDocsTab: "📚 Uploaded Docs",
    hidePanel: "Hide ◀",
    hidePanel: "Hide ◀",
    getStartedTitle: "How would you like to get started?",
    uploadDocLabel: "Upload Doc",
    uploadDocHint: "PDF, Word, PowerPoint",
    uploadedDocsLabel: "Uploaded Docs",
    noDocsYet: "No docs yet",
    yourUploadedDocs: "Your Uploaded Documents",
    noDocsUploaded: "No documents uploaded yet.",
    sessionWarning: "⚠️ Session only — this file will be removed from our servers when you close or leave the page",
    noDocLoaded: "No document loaded",
    couldNotLoad: "Could not load document",
    backendHint: "Make sure the backend is running",
    uploadingLabel: "Uploading…",
    processingLabel: "Processing, please wait…",
    deleteDocTitle: "Delete this document permanently? This cannot be undone.",

    // ProfileModal
    thisMonthUsage: "This Month's Usage",
    voiceLabel: "🎙️ Voice",
    aiLabel: "✨ AI",
    manageSubscription: "Manage Subscription →",
    planExpired: "Your plan has expired.",
    renewPlan: "Renew Plan →",
    profileTitle: "Profile",
    allOptional: "(all optional)",
    nameLabel: "Name",
    schoolLabel: "School",
    majorLabel: "Major",
    namePlaceholder: "Your name",
    schoolPlaceholder: "Your school or university",
    majorPlaceholder: "Your field of study",
    saveProfile: "Save Profile",
    savingLabel: "Saving…",
    savedLabel: "✓ Saved!",
    signOut: "Sign Out",

    // GeneralChatPage
    backHome: "← Home",
    generalChat: "General Chat",

    // HomePage
    heroSubtitle: "Your AI-Powered Learning Assistant",
    heroTagline: "Upload any textbook. Let AI explain, summarise, translate, and quiz you —\nso you can focus on actually learning.",
    btnOpenReader: "📖 Open Reader",
    btnLiveTranslation: "🎙️ Live Translation",
    studySmarter: "Everything you need to study smarter",
    planBadgeTrial: "🎉 Free Trial — ends",
    adminBtn: "⚙️ Admin",

    // Features
    features: [
      { icon: "📖", title: "Smart PDF Reader", desc: "Upload your textbooks and course materials. Read, annotate, and navigate with a built-in table of contents.", color: "#4f6ef7" },
      { icon: "💡", title: "AI Explanations", desc: "Struggling with a concept? Get clear, detailed explanations with real-world examples — or simplified for a beginner.", color: "#7c3aed" },
      { icon: "📝", title: "Instant Summaries", desc: "Summarise any page range into concise notes. Perfect for revision before exams.", color: "#22c55e" },
      { icon: "🌍", title: "Page Translation", desc: "Translate textbook pages into your native language while keeping the original side-by-side.", color: "#f59e0b" },
      { icon: "💬", title: "Chat with Your Book", desc: "Ask questions about your material and get AI-powered answers grounded in your actual textbook content.", color: "#ef4444" },
      { icon: "📋", title: "Organised Notes", desc: "Save AI outputs and your own notes into folders. Export to Word with one click.", color: "#0ea5e9" },
    ],

    // SubjectModal
    newSubjectTitle: "📚 New Subject",
    editSubjectTitle: "📚 Edit Subject",
    subjectNameLabel: "Subject Name",
    subjectNamePlaceholder: "e.g. Mathematics, Physics…",
    chooseBooksLabel: "Choose Books",
    noBooksAvailable: "No books available yet.",
    createSubjectBtn: "📚 Create Subject",
    saveChangesBtn: "💾 Save Changes",

    // Book card
    deleteBookConfirm: "Delete this book? This cannot be undone.",

    // LegalModals footer
    footerBy: "By using NoteMila, you agree to our",
    footerAnd: "and",
    termsOfService: "Terms of Service",
    privacyPolicy: "Privacy Policy",

    // PaywallPage
    backBtn: "← Back",
    choosePlan: "Choose Your Plan",
    currentPlan: "Current Plan",
    trialBanner: "You're on a free trial",
    trialEnds: "Trial ends",
    upgradeToUnlock: "Upgrade to unlock full access",
    mostPopular: "Most Popular",
    perMonth: "/month",
    subscribeBtn: "Subscribe",
    alipayWechatBtn: "Pay with Alipay / WeChat",
    manageBtn: "Manage Billing",
    usageThisMonth: "Usage This Month",
    networkError: "Network error. Please try again.",

    // BookCreatorModal
    createBookTitle: "Create New Book",
    bookTitleLabel: "Book Title",
    bookTitlePlaceholder: "Enter book title…",
    bookContentLabel: "Content (optional)",
    createBookBtn: "✨ Create Book",
    creatingBook: "Creating…",

    // AdminBillingPage
    adminTitle: "Admin — Billing",
    adminDashboardTitle: "Admin — Billing Dashboard",
    backBtnAdmin: "← Back",
    refreshBtn: "↻ Refresh",

    // NotesTab
    notesEmpty: "No notes yet. Generate some AI responses and save them here!",
    notesEmptyTitle: "No notes yet.",
    notesEmptyHint: "Create a folder first, then add notes inside it.",
    addNoteBtn: "+ Note",
    addFolderBtn: "📁 Folder",
    newestFirst: "Newest first",
    oldestFirst: "Oldest first",
    folderNamePlaceholder: "Folder name...",
    createBtn: "Create",
    dropHereNotes: "Drop notes here",
    dropHereFolders: "Drop notes or folders here",
    generalFolderLabel: "📂 General",
    saveNoteBtn: "✔ Save",
    insertLinkBtn: "Insert",
    newFolder: "+ New Folder",
    exportWord: "📥 Export to Word",
    searchNotes: "Search notes…",
    untitled: "Untitled",
    noSubfolders: "No subfolders",
    deleteNote: "Delete note",
    renameNote: "Rename",
    moveNote: "Move to…",
    generalFolder: "General",

    // PaywallPage additional
    trialEndedStatus: "Your free trial has ended",
    trialEndedHint: "Choose a plan below to continue using NoteMila.",
    trialActiveExpires: "Free trial active — expires",
    trialActiveHint: "Subscribe now to keep access after your trial ends. No charge until then.",
    paidStatusPrefix: "You're on the",
    paidStatusSuffix: "plan",
    paidHint: "Manage, upgrade, or cancel your subscription below.",
    voiceUsageLabel: "🎤 Voice translations",
    aiUsageLabel: "🤖 AI calls",
    reactivateAccount: "Reactivate your account",
    redirectingLabel: "Redirecting…",
    orPayOnce: "— or pay once with —",
    manageSubscriptionTitle: "Manage subscription",
    manageSubHint: "Cancel, update payment method, or upgrade / downgrade your plan.",
    openBillingPortal: "Open Billing Portal →",
    openingLabel: "Opening…",

    // BookEditorPage
    bookEditorTitlePlaceholder: "Book title…",
    bookPreviewBtn: "👁 Preview",
    bookEditModeBtn: "✏️ Edit",
    bookEditHint: "Click anywhere in the book to start typing",
    bookSaveBtn: "💾 Save",

    // BookCreatorModal additional
    createBookModalTitle: "📖 Create a New Book",
    bookTitlePlaceholderBcm: "e.g. Chapter 3 — Newton's Laws",
    bookContentTitle: "Content",
    bookContentSubhint: "(paste or type your text)",
    bookContentPlaceholder: "Paste notes, textbook pages, lecture content… AI will structure it into a beautiful book.",
    bookImagesTitle: "Images",
    bookImagesSubhint: "(paste Ctrl+V anywhere, drag & drop, or click Browse)",
    bookDragDropPlaceholder: "📷 Drag & drop images here",
    browseBtn: "Browse",
    addMoreBtn: "+ Add more",
    createBookError: "Please paste some text or add images to create a book.",
    generateBookBtn: "✨ Generate Book",
    generatingLabel: "Generating…",
  },

  zh: {
    langToggle: "English",

    loading: "加载中…",

    cancel: "取消",
    delete: "删除",
    confirm: "确认",
    open: "打开",
    back: "返回",

    myProfile: "我的资料",

    tabExplain: "💡 讲解",
    tabSummary: "📝 总结",
    tabTranslate: "🌍 翻译",
    tabChat: "💬 对话",
    tabNotes: "📋 笔记",
    noBookNotice: "请上传文档以使用此功能",

    pageRangeLabel: "页码范围",
    pageRangeTo: "至",
    promptBtn: "⚙️ 提示词",
    promptBtnTooltip: "设置提示词模板，自定义解释风格",
    ptTitle: "提示词模板设置",
    ptSubtitle: "自定义 AI 回复风格，选择预设或自写模板。",
    ptPresets: "预设",
    ptMyPresets: "我的模板",
    ptBuiltIn: "内置",
    ptCustomPreset: "自定义预设",
    ptTemplateEditor: "模板编辑器",
    ptCustomBadge: "自定义",
    ptResetDefault: "恢复默认",
    ptAvailableVars: "可用变量：",
    ptSaveAsPreset: "保存到我的模板",
    ptPresetNamePlaceholder: "预设名称...",
    ptAdd: "添加",
    ptCancel: "取消",
    ptSaveApply: "保存并应用",
    summarizeBtn: "📝 生成摘要",
    followUpPlaceholder: "提问跟进问题...",
    askBtn: "↩ 提问",
    invalidPageRange: "页码范围无效",
    tooManyPagesSummary: "每次最多总结 5 页",
    tooManyPagesTranslate: "每次最多翻译 5 页",
    maxPagesHint: "（每次最多 5 页）",
    summaryFailed: "生成摘要失败",
    failSummary: "无法生成摘要",
    failFollowUp: "无法发送跟进问题",
    followUpFailed: "跟进问题失败",

    numQuestionsLabel: "问题数量",
    generateQuizBtn: "🎯 生成测验",
    quizFailed: "生成测验失败",
    failQuiz: "无法生成测验",

    targetLanguageLabel: "目标语言",
    translateBtn: "🌍 翻译",
    translationFailed: "翻译失败",
    failTranslate: "无法翻译",

    explainConceptLabel: "您想了解哪个概念？",
    conceptPlaceholder: "例如：机器学习、光合作用、供需关系…",
    explainBtn: "💡 讲解",
    explainSimpleBtn: "🌱 简单讲解",
    followUpPlaceholderExplain: "追问或附加文件...",
    enterConceptFirst: "请输入一个概念或附加文件",
    explainFailed: "讲解失败",
    failExplain: "无法讲解该概念",

    chatEmptyTitle: "您可以提问任何内容 — 文字、图片或文件。",
    chatEmptyHint: "支持附加图片、PDF、Word、Excel、CSV 或 PowerPoint 文件。",
    chatInputPlaceholder: "在此提问，或粘贴图片 (Ctrl+V)...",
    clearChat: "🗑 清除对话",
    addToNotes: "📌 添加到笔记",
    chatError: "抱歉，出现了问题，请重试。",

    uploadTab: "📤 上传",
    uploadedDocsTab: "📚 已上传文档",
    hidePanel: "隐藏 ◀",
    hidePanel: "隐藏 ◀",
    getStartedTitle: "您想如何开始？",
    uploadDocLabel: "上传文档",
    uploadDocHint: "PDF、Word、PowerPoint",
    uploadedDocsLabel: "已上传文档",
    noDocsYet: "暂无文档",
    yourUploadedDocs: "您的已上传文档",
    noDocsUploaded: "尚未上传任何文档。",
    sessionWarning: "⚠️ 仅限本次会话 — 离开或关闭页面后，此文件将从服务器删除",
    noDocLoaded: "未加载文档",
    couldNotLoad: "无法加载文档",
    backendHint: "请确保后端正在运行",
    uploadingLabel: "上传中…",
    processingLabel: "处理中，请稍候…",
    deleteDocTitle: "永久删除此文档？此操作不可撤销。",

    thisMonthUsage: "本月使用量",
    voiceLabel: "🎙️ 语音",
    aiLabel: "✨ AI",
    manageSubscription: "管理订阅 →",
    planExpired: "您的套餐已过期。",
    renewPlan: "续订套餐 →",
    profileTitle: "个人资料",
    allOptional: "（均可选填）",
    nameLabel: "姓名",
    schoolLabel: "学校",
    majorLabel: "专业",
    namePlaceholder: "您的姓名",
    schoolPlaceholder: "您的学校或大学",
    majorPlaceholder: "您的学习领域",
    saveProfile: "保存资料",
    savingLabel: "保存中…",
    savedLabel: "✓ 已保存！",
    signOut: "退出登录",

    backHome: "← 首页",
    generalChat: "通用对话",

    heroSubtitle: "您的 AI 学习助手",
    heroTagline: "英文课堂实时翻译，上传任何教材，让 AI 为您讲解、总结、翻译、记笔记 \n让您专注于学习本身。",
    btnOpenReader: "📖 打开阅读器",
    btnLiveTranslation: "🎙️ 实时翻译",
    studySmarter: "您的智能学习全套工具",
    planBadgeTrial: "🎉 免费试用 — 结束于",
    adminBtn: "⚙️ 管理员",

    features: [
      { icon: "🎙️", title: "英文课堂实时翻译", desc: "双语字幕，线下、网课、录播都适用。", color: "#4f6ef7" },
      { icon: "💡", title: "AI 智能讲解", desc: "遇到难懂的概念？获得清晰详细的解释，附带实例，或以初学者友好的方式呈现。", color: "#7c3aed" },
      { icon: "📝", title: "即时总结", desc: "将任意页码范围总结为简洁笔记，非常适合考前复习。", color: "#22c55e" },
      { icon: "🌍", title: "页面翻译", desc: "将教材页面翻译成您的母语，同时保留原文对照。", color: "#f59e0b" },
      { icon: "💬", title: "与书籍对话", desc: "针对学习资料提问，获取基于您实际教材内容的 AI 解答。", color: "#ef4444" },
      { icon: "📋", title: "整理笔记", desc: "将 AI 输出和您的笔记保存到文件夹，一键导出为 Word 文档。", color: "#0ea5e9" },
    ],

    newSubjectTitle: "📚 新建科目",
    editSubjectTitle: "📚 编辑科目",
    subjectNameLabel: "科目名称",
    subjectNamePlaceholder: "例如：数学、物理学…",
    chooseBooksLabel: "选择书籍",
    noBooksAvailable: "暂无可用书籍。",
    createSubjectBtn: "📚 创建科目",
    saveChangesBtn: "💾 保存更改",

    deleteBookConfirm: "删除此书？此操作不可撤销。",

    footerBy: "使用 NoteMila 即表示您同意我们的",
    footerAnd: "和",
    termsOfService: "服务条款",
    privacyPolicy: "隐私政策",

    backBtn: "← 返回",
    choosePlan: "选择您的套餐",
    currentPlan: "当前套餐",
    trialBanner: "您正在使用免费试用",
    trialEnds: "试用结束于",
    upgradeToUnlock: "升级以解锁完整访问权限",
    mostPopular: "最受欢迎",
    perMonth: "/月",
    subscribeBtn: "订阅",
    alipayWechatBtn: "支付宝 / 微信支付",
    manageBtn: "管理账单",
    usageThisMonth: "本月使用量",
    networkError: "网络错误，请重试。",

    createBookTitle: "创建新书",
    bookTitleLabel: "书名",
    bookTitlePlaceholder: "输入书名…",
    bookContentLabel: "内容（可选）",
    createBookBtn: "✨ 创建书籍",
    creatingBook: "创建中…",

    adminTitle: "管理员 — 账单",
    adminDashboardTitle: "管理员 — 账单控制台",
    backBtnAdmin: "← 返回",
    refreshBtn: "↻ 刷新",

    notesEmpty: "暂无笔记。生成 AI 回复后可保存到这里！",
    notesEmptyTitle: "暂无笔记。",
    notesEmptyHint: "先创建一个文件夹，再在里面添加笔记。",
    addNoteBtn: "+ 笔记",
    addFolderBtn: "📁 文件夹",
    newestFirst: "最新在前",
    oldestFirst: "最旧在前",
    folderNamePlaceholder: "文件夹名称...",
    createBtn: "创建",
    dropHereNotes: "拖放笔记至此",
    dropHereFolders: "拖放笔记或文件夹至此",
    generalFolderLabel: "📂 通用",
    saveNoteBtn: "✔ 保存",
    insertLinkBtn: "插入",
    newFolder: "+ 新建文件夹",
    exportWord: "📥 导出为 Word",
    searchNotes: "搜索笔记…",
    untitled: "未命名",
    noSubfolders: "无子文件夹",
    deleteNote: "删除笔记",
    renameNote: "重命名",
    moveNote: "移动到…",
    generalFolder: "通用",

    trialEndedStatus: "您的免费试用已结束",
    trialEndedHint: "请选择一个套餐以继续使用 NoteMila。",
    trialActiveExpires: "免费试用中 — 到期时间：",
    trialActiveHint: "现在订阅，试用结束后继续保持访问权限。试用期间不收费。",
    paidStatusPrefix: "您正在使用",
    paidStatusSuffix: "套餐",
    paidHint: "在下方管理、升级或取消您的订阅。",
    voiceUsageLabel: "🎤 语音翻译",
    aiUsageLabel: "🤖 AI 调用",
    reactivateAccount: "重新激活您的账户",
    redirectingLabel: "跳转中…",
    orPayOnce: "— 或一次性付款 —",
    manageSubscriptionTitle: "管理订阅",
    manageSubHint: "取消、更新付款方式，或升级/降级您的套餐。",
    openBillingPortal: "打开账单门户 →",
    openingLabel: "打开中…",

    bookEditorTitlePlaceholder: "书名…",
    bookPreviewBtn: "👁 预览",
    bookEditModeBtn: "✏️ 编辑",
    bookEditHint: "点击书中任意位置开始输入",
    bookSaveBtn: "💾 保存",

    createBookModalTitle: "📖 创建新书",
    bookTitlePlaceholderBcm: "例如：第3章 — 牛顿定律",
    bookContentTitle: "内容",
    bookContentSubhint: "（粘贴或输入文字）",
    bookContentPlaceholder: "粘贴笔记、教材页面、讲座内容… AI 将自动整理成精美书籍。",
    bookImagesTitle: "图片",
    bookImagesSubhint: "（Ctrl+V 粘贴、拖放或点击浏览）",
    bookDragDropPlaceholder: "📷 将图片拖放至此",
    browseBtn: "浏览",
    addMoreBtn: "+ 添加更多",
    createBookError: "请粘贴文字或添加图片以创建书籍。",
    generateBookBtn: "✨ 生成书籍",
    generatingLabel: "生成中…",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    return localStorage.getItem("notemila-lang") || "zh";
  });

  const toggleLang = () => {
    setLang((prev) => {
      const next = prev === "en" ? "zh" : "en";
      localStorage.setItem("notemila-lang", next);
      return next;
    });
  };

  const t = (key) => translations[lang][key] ?? translations["en"][key] ?? key;

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
